import { useCallback, useEffect, useRef, useState } from "react";

import { Sparkles, User } from "lucide-react";
import useLocalStorageState from "use-local-storage-state";

import { Button } from "@/components/ui/button";
import { SmoothScrollArea } from "@/components/ui/smooth-scroll-area";
import {
  CompressedTable,
  CompressedTableBody,
  CompressedTableCell,
  CompressedTableRow,
} from "@/components/ui/table-compressed";

import { useGroupedTranscript } from "../../hooks/useGroupedTranscript";
import type { TranscriptSentence } from "../../utils/transcriptProcessor";
import { useCallContext } from "../Call/CallContext";
import { CloseTabButton } from "../CloseTabButton";
import {
  type ParticipantRoleFilter,
  ParticipantRoleFilterDropdown,
} from "../Filter/ParticipantRoleFilterDropdown";
import { LatencyDisplay } from "../LatencyDisplay";
import { PanelHeader } from "../PanelHeader";

export interface Speaker {
  initials: string;
  name: string;
  id: number;
  color: string;
}

export function TranscriptTable() {
  const { handleGetAIHelp, transcript, participants, participantRoles } =
    useCallContext();
  const [autoScrollPaused, setAutoScrollPaused] = useState(false);
  const autoScrollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollAreaRef = useRef<
    HTMLDivElement & { smoothScrollTo: (options?: ScrollToOptions) => void }
  >(null);
  useEffect(() => {
    scrollToBottom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!autoScrollPaused) {
      scrollToBottom();
    }
  }, [autoScrollPaused]);

  const handleWheel = () => {
    setAutoScrollPaused(true);
    if (autoScrollTimer.current) {
      clearTimeout(autoScrollTimer.current);
    }
    autoScrollTimer.current = setTimeout(() => {
      setAutoScrollPaused(false);
    }, 30000);
  };

  const handleGetSentenceHelp = useCallback(
    (sentence: TranscriptSentence) => {
      console.log("Getting help for sentence:", sentence, transcript);
      handleGetAIHelp({
        transcriptItemIds: sentence.item_ids,
      });
    },
    [handleGetAIHelp, transcript],
  );

  const scrollToBottom = () => {
    setTimeout(() => {
      if (scrollAreaRef.current) {
        if (scrollAreaRef.current.smoothScrollTo) {
          scrollAreaRef.current.smoothScrollTo({
            top: scrollAreaRef.current.scrollHeight,
          });
        }
      }
    }, 100);
  };

  const getSpeakerInitials = (name: string) =>
    name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();

  const speakers = transcript.reduce(
    (acc, item) => {
      if (!acc[item.speaker_id]) {
        const initials = getSpeakerInitials(item.speaker);
        const count = Object.values(acc).filter((s) =>
          s.initials.startsWith(initials),
        ).length;
        acc[item.speaker_id] = {
          initials: count > 0 ? `${initials}${count + 1}` : initials,
          name: item.speaker,
          id: item.speaker_id,
          color: `hsl(${(item.speaker_id * 137) % 360}, 70%, 40%)`,
        };
      }
      return acc;
    },
    {} as Record<number, Speaker>,
  );

  const [displayFilter, setDisplayFilter] =
    useLocalStorageState<ParticipantRoleFilter>("transcript-display-filter", {
      defaultValue: "customer",
    });

  // Helper function to get participant role
  const getParticipantRole = (
    speakerId: number,
  ): "salesperson" | "customer" | "observer" | "unknown" => {
    const participantInfo = participantRoles?.participants.find(
      (p) => p.id === speakerId,
    );
    if (participantInfo) {
      return participantInfo.role === "client"
        ? "customer"
        : participantInfo.role;
    }

    // Fallback to host/guest logic for unassigned participants
    const participant = participants.find((p) => p.id === speakerId);
    if (!participant) {
      return "unknown";
    }

    return participant.is_host ? "salesperson" : "customer";
  };

  const filteredTranscript = transcript.filter((item) => {
    if (displayFilter === "all") {
      return true;
    }

    const participantRole = getParticipantRole(item.speaker_id);
    return participantRole === displayFilter;
  });

  const groupedTranscript = useGroupedTranscript(filteredTranscript);

  return (
    <div className="flex h-full w-full flex-col">
      <PanelHeader title="Transcript">
        <div className="flex flex-row items-center justify-between space-x-2">
          <div className="flex flex-row space-x-1 p-1">
            <div className="text-sm">{participants.length}</div>
            <div className="p-0.5">
              <User size={16} />
            </div>
          </div>
          <div className="flex-grow-0">
            <ParticipantRoleFilterDropdown
              value={displayFilter}
              onSelect={setDisplayFilter}
            />
          </div>
          <div className="flex-grow-0">
            <CloseTabButton />
          </div>
        </div>
      </PanelHeader>
      <LatencyDisplay />

      <SmoothScrollArea
        ref={scrollAreaRef}
        className="h-full flex-grow"
        smoothScroll
        onWheel={handleWheel}
      >
        <CompressedTable>
          <CompressedTableBody>
            {groupedTranscript.map((sentence) => (
              <TranscriptTableItem
                speakers={speakers}
                key={sentence.id}
                sentence={sentence}
                handleGetSentenceHelp={handleGetSentenceHelp}
              />
            ))}
          </CompressedTableBody>
        </CompressedTable>
      </SmoothScrollArea>
    </div>
  );
}

export interface TranscriptTableItemProps {
  sentence: TranscriptSentence;
  handleGetSentenceHelp: (sentence: TranscriptSentence) => void;
  speakers: Record<number, Speaker>;
}

export function TranscriptTableItem({
  sentence,
  handleGetSentenceHelp,
  speakers,
}: Readonly<TranscriptTableItemProps>) {
  const speaker = speakers[sentence.speaker_id];

  return (
    <CompressedTableRow key={sentence.id} className="align-top">
      <CompressedTableCell className="w-6 align-top">
        <div className="p-1">
          <div
            className={
              "mt-[0.25em] inline-block rounded-full px-1 py-0.5 text-center text-xs text-white"
            }
            style={{ backgroundColor: speaker.color }}
          >
            <span className="inline-block min-w-[3ch]">{speaker.initials}</span>
          </div>
        </div>
      </CompressedTableCell>
      <CompressedTableCell>{sentence.sentence}</CompressedTableCell>
      <CompressedTableCell className="text-right align-top">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            handleGetSentenceHelp(sentence);
          }}
          aria-label="Get help"
        >
          <Sparkles size={16} />
        </Button>
      </CompressedTableCell>
    </CompressedTableRow>
  );
}
